/**
 * Settings → Messaging's words and states (slice 2e spec §6.1–§6.3; Paper
 * 60–65), apart from the components so they're tested alone.
 */
import { ApiError, errorHint } from "../../app/api";
import { countryOfZone, e164In, type Country } from "../../../worker/messaging/phone";
import type { Link, MadeLink, Messaging, MessagingApp } from "./api";
import type { Tone } from "../settings/integrations-view";

/** A number as people write it: `+1 (226) 224-6894` in NANP; others, and emails, as they are. */
export function shownHandle(handle: string): string {
  const nanp = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(handle);
  return nanp ? `+1 (${nanp[1]}) ${nanp[2]}-${nanp[3]}` : handle;
}

/** Minutes and seconds left: `9:12`; `0:00` once past. */
export function countdown(until: string | null, now: number): string {
  const left = until ? Math.max(0, Math.ceil((Date.parse(until) - now) / 1000)) : 0;
  return `${Math.floor(left / 60)}:${String(left % 60).padStart(2, "0")}`;
}

const past = (until: string | null, now: number) => !until || Date.parse(until) <= now;

export type RowState =
  | { kind: "unavailable" }
  | { kind: "not_linked" }
  | { kind: "pending"; link: Link }
  | { kind: "expired"; link: Link }
  | { kind: "confirming"; link: Link }
  | { kind: "linked"; link: Link };

/**
 * The row's state (spec §6.1). A code past its 10 minutes is expired here at
 * once; the server deletes it on the next load. A relink in progress shows as
 * that link; once it expires or goes, the linked one shows again.
 */
export function rowState(app: MessagingApp, available: boolean, now: number): RowState {
  if (!available) return { kind: "unavailable" };
  const link = app.link;
  if (link && link.status !== "linked") {
    const expired = past(link.expires_at, now);
    // A relink's code that ran out leaves the link as it was (spec §3).
    if (!expired) return { kind: link.status, link };
    if (!app.linked) return { kind: "expired", link };
  }
  return app.linked ? { kind: "linked", link: app.linked } : { kind: "not_linked" };
}

/** The status lane's words and tone (Paper 65). */
export function statusOf(state: RowState): { text: string; tone: Tone } {
  switch (state.kind) {
    case "unavailable":
      return { text: "Unavailable", tone: "grey" };
    case "not_linked":
      return { text: "Not linked", tone: "grey" };
    case "pending":
      return { text: "Waiting for your text", tone: "copper" };
    case "expired":
      return { text: "Code expired", tone: "grey" };
    case "confirming":
      return { text: "Is this you?", tone: "copper" };
    case "linked":
      return { text: "Linked", tone: "moss" };
  }
}

/** The status under the name on a phone (Paper 60b): a linked row names its handle. */
export function phoneStatus(state: RowState): string {
  if (state.kind === "linked")
    return `Linked · ${shownHandle(state.link.handle ?? state.link.number)}`;
  return statusOf(state).text;
}

/** The row's line under "iMessage". `code`: this tab's, when it holds one. */
export function detailOf(state: RowState, now: number, code: string | null): string {
  switch (state.kind) {
    case "unavailable":
      return "iMessage isn't available right now.";
    case "not_linked":
      return "Text your Chief of Staff from your iPhone.";
    case "pending": {
      const left = countdown(state.link.expires_at, now);
      const line = shownHandle(state.link.line);
      return code
        ? `Text ${code} to ${line} · ${left} left`
        : `Text your code to ${line} · ${left} left`;
    }
    case "expired":
      return "The code ran out before it arrived. Nothing was linked.";
    case "confirming":
      return `We got your code from ${state.link.handle ?? "another address"}. Is that you?`;
    case "linked":
      return `From ${shownHandle(state.link.handle ?? state.link.number)} · you text ${shownHandle(state.link.line)}`;
  }
}

/**
 * The page's data with a link just made: in place of the app's link in
 * progress, never its code (that stays in memory, `heldCodes`). A relink's
 * linked one stays.
 */
export function withLink(data: Messaging, made: MadeLink): Messaging {
  const { code: _code, sms_url: _sms, ...link } = made;
  return { ...data, apps: data.apps.map((a) => (a.app === link.app ? { ...a, link } : a)) };
}

/**
 * The page's data with a link gone (Cancel, Unlink, No, an email's Remove): a relink's code
 * cancelled leaves the linked one as the app's link; the linked one unlinked
 * leaves nothing.
 */
export function withoutLink(data: Messaging, id: string): Messaging {
  return {
    ...data,
    apps: data.apps.map((a) => {
      const linked = a.linked?.id === id ? null : a.linked;
      const link = a.link?.id === id ? linked : a.link;
      return { ...a, link, linked };
    }),
    // An email address removed or its code cancelled (slice 2h §4).
    ...(data.email
      ? { email: { ...data.email, links: data.email.links.filter((l) => l.id !== id) } }
      : {}),
  };
}

/** The page's data with a link just linked (Yes, that's me): the app's link and its linked one. */
export function withLinked(data: Messaging, link: Link): Messaging {
  return {
    ...data,
    apps: data.apps.map((a) => (a.app === link.app ? { ...a, link, linked: link } : a)),
  };
}

/** Where the link window is, for the link it made (spec §6.2). */
export type WindowStep = "code" | "expired" | "confirming" | "linked" | "gone";

/**
 * The window's step for its link, from the page's data: the hub event
 * refetches it when the code arrives. `gone`: cancelled, declined or tidied.
 */
export function windowStep(app: MessagingApp | undefined, linkId: string, now: number): WindowStep {
  if (app?.linked?.id === linkId) return "linked";
  const link = app?.link?.id === linkId ? app.link : null;
  if (!link) return "gone";
  if (link.status === "linked") return "linked";
  if (past(link.expires_at, now)) return "expired";
  return link.status === "confirming" ? "confirming" : "code";
}

/** Step 1's line (Paper 61, 61b): a relink says the old link keeps working. */
export function numberLine(linked: Link | null, mobile: boolean): string {
  if (linked) {
    return `Texts keep coming from ${shownHandle(linked.handle ?? linked.number)} until the new number links.`;
  }
  return mobile
    ? "Text your Chief of Staff from this phone. Replies come back as texts."
    : "Text your Chief of Staff from your iPhone. Replies come back as texts, and each thing you text about becomes its own chat here.";
}

/** Step 2's line (Paper 62, 62b), per what it shows. */
export function codeLine(mode: "code" | "lost" | "expired" | "confirming"): string {
  if (mode === "expired") return "The code ran out before it arrived. Nothing was linked.";
  if (mode === "lost") return "For your safety the code shows only once. Get a new one to text.";
  if (mode === "confirming") return "We got a text with this code. Confirm it was you.";
  // Messages on a Mac opens the same sms: link, so this isn't only for an iPhone.
  return "Send it from Messages. We'll link the number it comes from.";
}

/** The phone field's first country: the workspace timezone's, else the US. */
export function defaultCountry(timezone: string): Country {
  return countryOfZone(timezone) ?? { iso: "US", code: "1" };
}

const international = (input: string) => /^\s*(\+|00)/.test(input);

/**
 * What's sent for the number typed: E.164 in the chosen country. A national
 * number that can't be made E.164 here is null, refused in the window: sent
 * as typed, the server would read it in the workspace timezone's country,
 * not the one chosen. An international one is sent as typed for the server
 * to judge (`400 invalid`).
 */
export function numberFor(input: string, country: Country): string | null {
  const number = e164In(input, country);
  if (number) return number;
  return international(input) ? input.trim() : null;
}

/**
 * Why a number was refused, in the window's words. A national number in a
 * country whose mobiles can't be made E.164 here (Argentina, Mexico) needs its +.
 */
export function refusedHint(country: Country, input: string): string {
  if (country.needsPlus && !international(input)) {
    return `Include the country code: start with +${country.code}, as your phone shows it.`;
  }
  return "That doesn't look like a mobile number. Check it, with its country code.";
}

/** A link's error, in the window's words: a refused number as `refusedHint` says. */
export function linkErrorHint(error: unknown, country: Country, input: string): string {
  if (error instanceof ApiError && error.code === "invalid") return refusedHint(country, input);
  return errorHint(error);
}

/** A thread header's line (spec §6.3; slice 2h §8.3): texted or emailed, or since when it isn't. */
export function threadLine(
  messaging: { app?: string; linked: boolean; last_text_at: string | null },
  timeZone: string,
  mine: boolean,
  person: string | null,
): string {
  const email = messaging.app === "email";
  const verb = email ? "emailed" : "texted";
  if (messaging.linked) {
    return mine
      ? `Replies here are also ${verb} to you`
      : `Replies here are also ${verb} to ${person ?? "them"}`;
  }
  if (!messaging.last_text_at) return "Not linked.";
  const date = new Date(messaging.last_text_at).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
    timeZone,
  });
  return email ? `Not linked. Emails stopped ${date}.` : `Not linked. Texts stopped ${date}.`;
}

/**
 * A thread's messaging header (spec §6.3), or null for any other thread: its
 * line, and Link again for its own person once the link is gone.
 */
export function messagingHeader(
  detail: {
    thread: { messaging_app?: string | null; started_by_user_id: string | null };
    messaging?: { app?: string; linked: boolean; last_text_at: string | null } | null;
  },
  me: { user: { id: string }; workspace: { timezone: string } },
  person: string | null = null,
): { line: string; linkAgain: boolean } | null {
  if (!detail.thread.messaging_app || !detail.messaging) return null;
  const mine = detail.thread.started_by_user_id === me.user.id;
  return {
    line: threadLine(detail.messaging, me.workspace.timezone, mine, person),
    linkAgain: mine && !detail.messaging.linked,
  };
}
